import { get, noop } from 'lodash';
import { ColumnMenu } from 'taboola-ultimate-ui';
import { normalizeCellEditor } from '../components/CellEditors';
import { withEditIndication, withRecommendationIndication } from '../components/CellRenderers';
import { withCellRenderTracker } from '../components/CellRenderers/withCellRenderTracker';
import { editableColumn } from '../config/columns';
import columnTypesMap from '../config/columns/columnTypes';
import { emptyComparator } from './emptyComparator';
import generateColumnId from './generateColumnId';
import suppressKeyboardEvent from './suppressKeyboardEvent';

// Here we are assuming that default string value getter is used for nested values as per ag-grid spec.
const sanitizeValueGetter = stringValueGetter => params => get(params, stringValueGetter, null);

const normalizeColumnDefs = (
    columnDefs = [],
    reportId,
    editIndicationHoc = withEditIndication,
    recommendationIndicationHoc = withRecommendationIndication,
    cellEditorHoc = normalizeCellEditor
) =>
    columnDefs.map(colDef => {
        const { type, cellRendererParams = {}, field, referenceColId } = colDef;
        const pinExtension = type
            ? type.reduce((acc, typeName) => {
                  if (columnTypesMap[typeName]?.pinned) {
                      return { ...acc, ...columnTypesMap[typeName], lockPosition: columnTypesMap[typeName]?.pinned };
                  }
                  return acc;
              }, {})
            : {};
        const extendedColumnDef = {
            ...colDef,
            colId: generateColumnId(reportId, field),
            comparator: emptyComparator,
            suppressFiltersToolPanel: true,
            cellDataType: false,
            filter: ColumnMenu,
            ...pinExtension,
        };

        if (referenceColId) {
            extendedColumnDef.referenceColId = generateColumnId(reportId, referenceColId);
        }

        if (typeof extendedColumnDef.valueGetter === 'string') {
            extendedColumnDef.valueGetter = sanitizeValueGetter(extendedColumnDef.valueGetter);
        }

        const isEditable = type && type.includes(editableColumn) && !cellRendererParams.hideEditIndication;

        if (colDef.cellRenderer) {
            extendedColumnDef.cellRenderer = withCellRenderTracker(extendedColumnDef.cellRenderer);
        }

        if (isEditable) {
            extendedColumnDef.cellRenderer = editIndicationHoc(extendedColumnDef.cellRenderer);
        }
        if (extendedColumnDef?.cellRendererParams?.recommendationTypes) {
            extendedColumnDef.cellRenderer = recommendationIndicationHoc(extendedColumnDef.cellRenderer);
        }
        if (colDef.cellEditor) {
            extendedColumnDef.cellEditor = cellEditorHoc(colDef.cellEditor);
            extendedColumnDef.cellEditorPopup = true;
            extendedColumnDef.suppressKeyboardEvent = suppressKeyboardEvent;
        }

        return extendedColumnDef;
    });

const emptyFooterColumnMapper = (colDef, reportId) => {
    const { field, permissions, accountConfigurations, visibilityCondition } = colDef;

    return {
        field,
        colId: generateColumnId(reportId, field),
        editable: false,
        valueFormatter: null,
        valueGetter: noop,
        headerComponent: null,
        cellEditor: null,
        cellRenderer: null,
        permissions,
        accountConfigurations,
        visibilityCondition,
    };
};

export const createFooterMapper = reportId => colDef => emptyFooterColumnMapper(colDef, reportId);

export default normalizeColumnDefs;
